/** Client-safe static data. Server-only tables (drops, upgrade rolls) live in ./server. */
import classesJson from "../json/classes.json";
import clockJson from "../json/clock.json";
import combatJson from "../json/combat.json";
import economyJson from "../json/economy.json";
import itemsJson from "../json/items.json";
// Local experiment (branch ko-assets-local): ko.json is written by `pnpm --filter
// @two-crowns/assets ko` from the ko-assets and ko-db repositories; the committed copy is empty
// and git is told to skip local changes to it (skip-worktree), so the data never gets committed.
import koFile from "../json/ko.json";
import mobsJson from "../json/mobs.json";
import moderationJson from "../json/moderation.json";
import movementJson from "../json/movement.json";
import npcsJson from "../json/npcs.json";
import progressionJson from "../json/progression.json";
import pvpJson from "../json/pvp.json";
import questsJson from "../json/quests.json";
import serversJson from "../json/servers.json";
import skillsJson from "../json/skills.json";
import socialJson from "../json/social.json";
import storeJson from "../json/store.json";
import structuresJson from "../json/structures.json";
import upgradeJson from "../json/upgrade.json";
import wheelJson from "../json/wheel.json";
import xpJson from "../json/xp.json";
import zonesJson from "../json/zones.json";
import {
  type ClassDef,
  ClassesSchema,
  type ClassId,
  ClockSchema,
  CombatSchema,
  EconomySchema,
  type ItemDef,
  type ItemStats,
  ItemsSchema,
  type MobDef,
  MobsSchema,
  ModerationSchema,
  MovementSchema,
  type NpcDef,
  NpcsSchema,
  ProgressionSchema,
  PvpSchema,
  type QuestDef,
  QuestsSchema,
  type ServerDef,
  ServersSchema,
  type SkillDef,
  SkillsSchema,
  SocialSchema,
  StoreSchema,
  type StructureDef,
  StructuresSchema,
  UpgradeTablesSchema,
  WheelSchema,
  XpTableSchema,
  type Zone,
  ZonesSchema,
} from "./schema";
import { validateReferences } from "./validate";

export {
  type DerivedStats,
  deriveStats,
  type EquippedItem,
  type GearBonus,
  gearBonus,
  JEWELRY_SLOTS,
  koLadder,
  levelShare,
  mobLevelMult,
  NO_UPGRADE_SLOTS,
  plusMultiplier,
  type StatBlock,
  upgradeTableFor,
} from "./derive";

import { deriveStats, upgradeTableFor as ladderFor, levelShare } from "./derive";

/**
 * Local experiment: ko.json, or nothing at all when KO_DATA=off (the game's own tests run on the
 * game's own data, vitest.config.ts; the browser has no `process` and always takes KO's).
 */
const koOff = typeof process !== "undefined" && process.env?.KO_DATA === "off";
const koJson = (koOff ? {} : koFile) as Partial<typeof koFile>;

export { sellPrice } from "./pricing";
export * from "./schema";
export { validateReferences } from "./validate";
export { buildXpTable, mobXpMult, xpForLevel } from "./xp";

/** On KO's maps ko.json `progression` overrides the game's (local experiment). */
export const progression = ProgressionSchema.parse({
  ...progressionJson,
  ...(((koJson.zones ?? []).length > 0 &&
    (koJson as { progression?: Record<string, unknown> }).progression) ||
    {}),
});
export const movement = MovementSchema.parse(movementJson);
/** KO's own XP table on KO's maps (local experiment). */
export const xpTable = XpTableSchema.parse(
  (koJson as { xp?: unknown[] }).xp?.length ? (koJson as { xp?: unknown[] }).xp : xpJson,
);
/**
 * The Crystal Clash arena is one map in zones.json played in one room per level bracket: it
 * loads as a copy per bracket, `<id>_1`, `<id>_2`... with `clash.bracket` set.
 */
function expandArenas(list: Zone[]): Zone[] {
  return list.flatMap((z) =>
    z.clash
      ? z.clash.brackets.map((_, i) => ({
          ...z,
          id: `${z.id}_${i + 1}`,
          clash: { ...(z.clash as NonNullable<Zone["clash"]>), bracket: i },
        }))
      : [z],
  );
}
/** Local experiment: with ko.json's maps in, the game is ko's alone (none of our zones). */
const koOnly = (koJson.zones ?? []).length > 0;
/** KO's maps replace the game's (local experiment). */
export const KO_ONLY = koOnly;
export const zones = expandArenas(
  ZonesSchema.parse(koOnly ? koJson.zones : [...zonesJson, ...(koJson.zones ?? [])]),
);
// Local experiment: ko.json's combat settings (ko's own formulas) over combat.json.
export const combat = CombatSchema.parse({
  ...combatJson,
  ...((koJson as { combat?: Record<string, unknown> }).combat ?? {}),
});

/**
 * A class's usual max MP at a level: its base stats with every stat point in its primary stat,
 * no gear. Skill costs and the blue potion follow it, so they grow as the pools do.
 */
export function typicalMaxMp(cls: ClassDef, level: number, cfg = combat): number {
  const stats = { ...cls.baseStats };
  stats[cls.primary] += progression.statPointsPerLevel * Math.max(0, level - 1);
  return deriveStats(cls, stats, cfg, {}, level).maxMp;
}

/** What a skill costs a caster of this class and level (classes.json `mpCostRef`). */
export function skillMpCost(skill: SkillDef, cls: ClassDef, level: number, cfg = combat): number {
  if (skill.mpCost <= 0) return 0;
  // KO's skills cost their own MSP (local experiment)
  if (cfg.ko) return skill.mpCost;
  return Math.max(1, Math.round((skill.mpCost * typicalMaxMp(cls, level, cfg)) / cls.mpCostRef));
}

/** What a character's endless potion restores at a level (HP, or MP for the class). */
export function potionAmount(
  kind: "hp" | "mp",
  level: number,
  cls: ClassDef,
  cfg = combat,
): number {
  const tier = koPotionTier(kind, level, cfg);
  if (tier) return tier[1];
  if (kind === "mp")
    return Math.round((typicalMaxMp(cls, level, cfg) * cfg.potions.mpPoolPct) / 100);
  // Pools grow with level (levelGrowth), so the potion does too.
  const grow = 1 + levelShare(level, cfg) * cfg.levelGrowth.potionBonus;
  return Math.round((cfg.potions.hpBase + cfg.potions.hpPerLevel * level) * grow);
}

/** KO's potion of a level (local experiment): [from level, amount, item], the highest reached. */
export function koPotionTier(
  kind: "hp" | "mp",
  level: number,
  cfg = combat,
): [number, number, string] | undefined {
  const list = cfg.koPotions?.[kind];
  if (!list?.length) return undefined;
  let best = list[0];
  for (const t of list) if (level >= t[0]) best = t;
  return best;
}

/**
 * KO's skill trees (local experiment, owner 2026-10-26): a job skill (a tree code ending in 5, 6
 * or 7) opens when that tree holds as many points as its level; points come from the
 * character's level (ko.json `skillPoints`); the common skills open by level alone.
 */
export const koTreeOf = (skill: { koTree?: number }): "5" | "6" | "7" | null => {
  const d = (skill.koTree ?? 0) % 10;
  return d === 5 || d === 6 || d === 7 ? (String(d) as "5" | "6" | "7") : null;
};
const koSkillPointRule = (
  koJson as {
    skillPoints?: {
      fromLevel: number;
      perLevel: number;
      resetFreeBelow?: number;
      resetGoldPerLevel?: number;
    };
  }
).skillPoints;
/** What taking back every point of KO's trees costs at this level (free below the floor). */
export const koSkillResetGold = (level: number): number =>
  !koSkillPointRule || level < (koSkillPointRule.resetFreeBelow ?? 0)
    ? 0
    : level * (koSkillPointRule.resetGoldPerLevel ?? 0);
/** All the tree points a character of this level has. */
export const koSkillPoints = (level: number): number =>
  koSkillPointRule && level >= koSkillPointRule.fromLevel
    ? (level - koSkillPointRule.fromLevel + 1) * koSkillPointRule.perLevel
    : 0;
/** Whether a character may use a skill: its tree's points (KO's job skills) or its level. */
export function skillLearned(
  skill: { koTree?: number; unlockLevel: number },
  level: number,
  tree?: Partial<Record<string, number>>,
): boolean {
  const d = koSkillPointRule ? koTreeOf(skill) : null;
  if (d) return (tree?.[d] ?? 0) >= skill.unlockLevel;
  return level >= skill.unlockLevel;
}

/**
 * Local experiment: KO's master passives a character of this class and level holds (they open
 * by themselves at their level, owner 2026-10-26): of each kind the strongest one counts
 * (Matchless's 15% replaces Absoluteness's 10%, KO does not add them up).
 */
export function koMasterPassives(
  cls: string,
  level: number,
): { damageTakenPct: number; lowHpDefPct: number; lowMpRegenPct: number } {
  const out = { damageTakenPct: 0, lowHpDefPct: 0, lowMpRegenPct: 0 };
  for (const s of skills)
    if (s.koPassive && s.classId === cls && level >= s.unlockLevel) {
      const p = s.koPassive;
      out.damageTakenPct = Math.min(out.damageTakenPct, p.damageTakenPct ?? 0);
      out.lowHpDefPct = Math.max(out.lowHpDefPct, p.lowHpDefPct ?? 0);
      out.lowMpRegenPct = Math.max(out.lowMpRegenPct, p.lowMpRegenPct ?? 0);
    }
  return out;
}

/** How long a potion of this kind takes to be ready again. */
export const potionCooldownMs = (kind: "hp" | "mp", cfg = combat): number =>
  kind === "mp" ? cfg.potions.mpCooldownMs : cfg.potions.cooldownMs;
export const clock = ClockSchema.parse(clockJson);

/** Game hour (0..24) at a real time, shared by everyone. */
export function gameHour(nowMs: number): number {
  return ((((nowMs / clock.dayLengthMs) * 24 + clock.hourOffset) % 24) + 24) % 24;
}
// Local experiment: ko.json's starters give each class its first ko weapon.
const koStarters = (koJson as { starters?: Record<string, unknown[]> }).starters ?? {};
const koClasses = (koJson as { classes?: Record<string, Record<string, unknown>> }).classes ?? {};
export const classes = ClassesSchema.parse(
  classesJson.map((c) => ({
    ...c,
    ...(koStarters[c.id] ? { starterItems: koStarters[c.id] } : {}),
    ...(koClasses[c.id] ?? {}),
  })),
);
/** KO's skills replace the game's own on a ko map set (local experiment). */
export const skills = SkillsSchema.parse(
  koOnly && (koJson as { skills?: unknown[] }).skills?.length
    ? (koJson as { skills?: unknown[] }).skills
    : skillsJson,
);
/** On KO's maps only KO's monsters (the database lists them; owner 2026-10-26). */
export const mobs = MobsSchema.parse(
  koOnly ? (koJson.mobs ?? []) : [...mobsJson, ...(koJson.mobs ?? [])],
);
export const structures = StructuresSchema.parse(structuresJson);
/**
 * On KO's maps only KO's items exist (owner, 2026-10-26: none of the game's own), and the Power
 * Up Store's own (wings, cloaks, glows, scrolls, its items) so the store keeps selling.
 */
const storeIds = new Set(
  [
    ...(storeJson as { glows: { item: string }[] }).glows,
    ...(storeJson as { wings: { item: string }[] }).wings,
    ...(storeJson as { cloaks: { item: string }[] }).cloaks,
    ...(storeJson as { items: { item: string }[] }).items,
  ].map((x) => x.item),
);
/**
 * The items the login calendar and the first week goals give (the Novice scrolls, worth
 * nothing) and the rankings' reward: kept on KO's maps too, or they would hand out blanks.
 */
const rewardIds = new Set(
  (
    [
      ...(economyJson as { dailyReward: { days: { items?: { item: string }[] }[] } }).dailyReward
        .days,
      ...(economyJson as { koDailyReward: { days: { items?: { item: string }[] }[] } })
        .koDailyReward.days,
      { items: (economyJson as { koNewAccountGift: { item: string }[] }).koNewAccountGift },
      ...(economyJson as { firstWeek: { goals: { items?: { item: string }[] }[] } }).firstWeek
        .goals,
    ] as { items?: { item: string }[] }[]
  )
    .flatMap((x) => (x.items ?? []).map((i) => i.item))
    // and the NP rankings' reward (the Herald stands in Moradon)
    .concat((pvpJson as { rankRewards: { item: string }[] }).rankRewards.map((r) => r.item)),
);
export const items = ItemsSchema.parse(
  koOnly
    ? [
        ...(koJson.items ?? []),
        ...itemsJson.filter((i) => storeIds.has(i.id) || rewardIds.has(i.id)),
      ]
    : [...itemsJson, ...(koJson.items ?? [])],
);
const economyBase = EconomySchema.parse(economyJson);
/**
 * KO's maps give their own new account gift and login calendar (owner 2026-10-29: the Novice
 * scrolls and KO's looks lent for days); no invite rewards of the game's items.
 */
export const economy = koOnly
  ? {
      ...economyBase,
      newAccountGift: economyBase.koNewAccountGift,
      dailyReward: economyBase.koDailyReward,
      referral: { ...economyBase.referral, referrer: [], referred: [] },
    }
  : economyBase;
export const npcs = NpcsSchema.parse(koOnly ? koJson.npcs : [...npcsJson, ...(koJson.npcs ?? [])]);
export const pvp = PvpSchema.parse(pvpJson);
export const social = SocialSchema.parse(socialJson);
/** On KO's maps ko.json `quests` (KO's own, local experiment) take the place of ours. */
export const questData = QuestsSchema.parse(
  koOnly ? { ...questsJson, quests: (koJson as { quests?: unknown[] }).quests ?? [] } : questsJson,
);
/** The Riftstone Wheel's segments (the odds are server-only). */
export const wheel = WheelSchema.parse(wheelJson);
const zoneIds = new Set(zones.map((z) => z.id));
/** Quests of the zones in the game (none of ours when ko's maps are in). */
export const quests = questData.quests.filter((q) => q.zones.some((z) => zoneIds.has(z)));
export const servers: ServerDef[] = ServersSchema.parse(serversJson);
export const getServer = (id: string): ServerDef | undefined => servers.find((s) => s.id === id);
/** KO's Power Up Store items on KO's maps (ko.json `pus`, local experiment), before the game's. */
const koPus = (koJson as { pus?: Record<string, unknown[]> }).pus;
export const store = StoreSchema.parse(
  koOnly && koPus
    ? {
        // KO's looks alone (owner 2026-10-30: nothing of the first world's left): the game's own
        // wings, glows, armor skins and cloaks are not sold; copies held keep their days
        ...storeJson,
        wings: koPus.wings ?? [],
        glows: koPus.glows ?? [],
        skins: koPus.skins ?? [],
        cloaks: [],
        // (no Fortune Stone: KO's Trina's Piece is the same luck, owner 2026-10-29); the Market
        // Scroll is the game's and stays
        items: [
          ...(koPus.items ?? []),
          ...storeJson.items.filter((i) => i.item !== "fortune_stone"),
        ],
      }
    : storeJson,
);
/** Item templates sold for gems (Wisps, Fortune Stones): they can never be thrown away. */
const STORE_ITEMS = new Set([
  // (the game's own, a Fortune Stone bought before KO's store dropped it included)
  ...storeIds,
  ...store.glows.map((g) => g.item),
  ...store.wings.map((w) => w.item),
  ...store.cloaks.map((c) => c.item),
  ...store.items.map((i) => i.item),
  ...store.skins.flatMap((s) => (s.item ? [s.item] : [])),
]);
export const isStoreItem = (templateId: string): boolean => STORE_ITEMS.has(templateId);
/**
 * Whether players may trade an item or sell it on a stall: anything worth gold, and of the
 * Power Up Store only what is used up (owner 2026-10-29: Trina's Pieces, the Experience,
 * Genie and Market Scrolls); looks, wings, glows and cloaks stay with their buyer. The Novice
 * scrolls and every reward (worth nothing, not store items) never move.
 */
export function playerTradeable(templateId: string): boolean {
  const def = getItem(templateId);
  if (def.bound) return false;
  if (def.value > 0) return true;
  return isStoreItem(templateId) && def.slot === "none" && !!(def.scroll || def.use || def.opens);
}
/**
 * Materials worth keeping: what a shop sells (Gold Bars, gold kept in the bag) and the Riftstones
 * the Reel spins. A merchant's "+ Materials" button never adds them to a sale.
 */
const KEPT_MATERIALS = new Set([
  ...npcs.flatMap((n) => (n.shop ?? []).map((o) => o.item)),
  ...Object.values(wheel.tiers).map((tier) => tier.gem),
]);
/** A monster's loot material, sold in bulk with the "+ Materials" button. */
export const isLootMaterial = (templateId: string): boolean =>
  getItem(templateId).kind === "material" && !KEPT_MATERIALS.has(templateId);
export const moderation = ModerationSchema.parse(moderationJson);

/** Clan names: letters, digits and single spaces, no leading or trailing space. */
export function validClanName(name: string): boolean {
  const { nameMin, nameMax } = social.clan;
  return (
    name.length >= nameMin && name.length <= nameMax && /^[A-Za-z0-9]+( [A-Za-z0-9]+)*$/.test(name)
  );
}

/** Rank symbol index for lifetime NP earned: 0 = none, 1 = pvp.ranks[0], ... */
/**
 * The one place symbol a player shows (owner decision 2026-10-01): the ranking of all time
 * comes first, its place boxed; without one, this month's place plain. Null without a symbol
 * place.
 */
export function shownPlace(month: number, all: number): { place: number; boxed: boolean } | null {
  if (placeSymbol(all)) return { place: all, boxed: true };
  if (placeSymbol(month)) return { place: month, boxed: false };
  return null;
}

/**
 * What a GM of this class shows (moderation.json gmLook): slot and item template, in the
 * order the game sends gear. A one-handed set weapon is taken first so a shield fits beside.
 */
export function gmGear(cls: ClassId): { slot: string; templateId: string }[] {
  const { tier, cape } = moderation.gmLook;
  const own = items.filter((i) => i.tier === tier && i.classes?.includes(cls));
  const pick = (slot: string) => {
    const of = own.filter((i) => i.slot === slot);
    return of.find((i) => !i.twoHanded) ?? of[0];
  };
  const out: { slot: string; templateId: string }[] = [];
  const weapon = pick("weapon");
  for (const slot of ["weapon", "offhand", "head", "chest", "hands", "feet"]) {
    const it = pick(slot);
    if (!it || (slot === "offhand" && weapon?.twoHanded)) continue;
    out.push({ slot, templateId: it.id });
  }
  out.push({ slot: "cape", templateId: cape });
  return out;
}

/** The place symbol (1-based index into pvp.placeSymbols) for a ranking place, or 0. */
export function placeSymbol(place: number): number {
  if (place < 1) return 0;
  return pvp.placeSymbols.findIndex((s) => place <= s.upTo) + 1;
}

export function npRank(npEarned: number): number {
  let rank = 0;
  pvp.ranks.forEach((r, i) => {
    if (npEarned >= r.minNp) rank = i + 1;
  });
  return rank;
}
/** PRD: upgrade odds are public (the blacksmith shows the exact chance); rolls stay on the server. */
/** On KO's maps a step's success takes ko.json `upgradePct` (step from +N: percent; owner 2026-10-26). */
const koUpgradePct = (koJson as { upgradePct?: Record<string, number> }).upgradePct;
export const upgradeTables = UpgradeTablesSchema.parse(
  koOnly && koUpgradePct
    ? {
        ...upgradeJson,
        gear: {
          ...upgradeJson.gear,
          steps: upgradeJson.gear.steps.map((st) => ({
            ...st,
            successPct: koUpgradePct[String(st.from)] ?? st.successPct,
          })),
        },
      }
    : upgradeJson,
);

validateReferences({
  zones,
  classes,
  skills,
  mobs,
  structures,
  items,
  npcs,
  quests,
  ...(koOnly ? {} : { wheel }),
  economy,
});

const byId = <T extends { id: string }>(list: T[], kind: string) => {
  const map = new Map(list.map((x) => [x.id, x]));
  return (id: string): T => {
    const found = map.get(id);
    if (!found) throw new Error(`Unknown ${kind}: ${id}`);
    return found;
  };
};

export const getZone: (id: string) => Zone = byId(zones, "zone");
/** Where new characters begin: Hearthmoor, or a zone marked `start` (local experiment, Moradon). */
export const START_ZONE_ID = zones.find((z) => z.start)?.id ?? "dev_field";
export const getQuest: (id: string) => QuestDef = byId(quests, "quest");

/**
 * Neutral ground: no PvP and no nation's homeland (Hearthmoor). Both nations may party and
 * trade there (owner decision 2026-10-01); elsewhere only their own nation.
 */
export function isNeutralZone(zone: Zone): boolean {
  return !zone.pvp && !zone.nation;
}

/** Where a character of this race arrives and respawns in a zone. */
export function spawnFor(zone: Zone, race: string): { x: number; z: number } {
  const bases = zone.bases;
  if (bases && (race === "human" || race === "orc")) return bases[race];
  return zone.spawn;
}
export const getClass: (id: ClassId) => ClassDef = byId(classes, "class");
export const getSkill: (id: string) => SkillDef = byId(skills, "skill");
export const getMob: (id: string) => MobDef = byId(mobs, "mob");
export const getStructure: (id: string) => StructureDef = byId(structures, "structure");
const itemIdSet = new Set(items.map((i) => i.id));

/** KO's names for an enchanted piece's kind, written before its name (local experiment). */
export const ENCHANT_KINDS = {
  str: "Strength",
  hp: "Health",
  dex: "Dexterity",
  int: "Intelligence",
  magic: "Magic",
  immune: "Resistant",
  elemental: "Elemental",
} as const;
/** KO's word for an Elemental Scroll's copy, by what it adds. */
const ELEMENT_WORDS: Record<string, string> = {
  fire: "Burning",
  ice: "Frozen",
  lightning: "Shocking",
  poison: "Poisonous",
  hpDrain: "Vampiric",
};
/** A weapon's elemental damage at its + (an Elemental Scroll's copy grows with it). */
export const elementDamage = (def: ItemDef, plus: number): number =>
  def.elementByPlus?.[Math.min(10, Math.max(0, plus))] ?? def.elementDmg ?? 0;
export type EnchantKind = keyof typeof ENCHANT_KINDS;
const enchantLadders = ((koJson as { enchants?: Record<string, ItemStats>[] }).enchants ??
  []) as Record<string, ItemStats>[];
const enchantedCache = new Map<string, ItemDef>();
/** The id of an enchanted copy of a piece: `<id>~<kind>`. */
export const enchantedId = (id: string, kind: EnchantKind) => `${baseItemId(id)}~${kind}`;
/** A piece's own item id, without its enchanted kind. */
export const baseItemId = (id: string) => {
  const at = id.indexOf("~");
  return at < 0 ? id : id.slice(0, at);
};
/**
 * An Enchant Scroll's copy of a KO piece (`<id>~<kind>`): the piece itself with that kind's +
 * ladder (its stat from +0 on) and KO's word for the kind before its name.
 */
function enchantedItem(id: string): ItemDef | undefined {
  const at = id.indexOf("~");
  if (at < 0) return undefined;
  const hit = enchantedCache.get(id);
  if (hit) return hit;
  const base = itemIdSet.has(id.slice(0, at)) ? getItemStrict(id.slice(0, at)) : undefined;
  const kind = id.slice(at + 1) as EnchantKind;
  const ladder = base?.koEnchant?.[kind];
  if (!base || ladder === undefined || !enchantLadders[ladder]) return undefined;
  // an Elemental Scroll's ladder carries its element's damage as "<element>Dmg"
  let plusBonus = enchantLadders[ladder] as Record<string, ItemStats>;
  let element: ItemDef["element"];
  const byPlus: number[] = [];
  for (let n = 0; n <= 10; n++)
    for (const [k, v] of Object.entries(plusBonus[String(n)] ?? {}))
      if (k.endsWith("Dmg")) {
        element = k.slice(0, -3) as ItemDef["element"];
        byPlus[n] = v as number;
      }
  if (element) {
    plusBonus = Object.fromEntries(
      Object.entries(plusBonus).map(([n, st]) => [
        n,
        Object.fromEntries(Object.entries(st).filter(([k]) => !k.endsWith("Dmg"))),
      ]),
    );
    for (let n = 0; n <= 10; n++) byPlus[n] = byPlus[n] ?? byPlus[n - 1] ?? 0;
  }
  const word =
    kind === "elemental"
      ? (ELEMENT_WORDS[element ?? (plusBonus["0"]?.hpDrain ? "hpDrain" : "")] ?? "Elemental")
      : ENCHANT_KINDS[kind];
  const def: ItemDef = {
    ...base,
    id,
    name: `${word} ${base.name}`,
    ...(element ? { element, elementDmg: Math.max(1, byPlus[0] ?? 1), elementByPlus: byPlus } : {}),
    plusBonus,
    plusBonusMagic: undefined,
    plusBonusRare: undefined,
    koNames: undefined,
    koEnchant: undefined,
    enchanted: kind,
  };
  enchantedCache.set(id, def);
  return def;
}
const getItemStrict: (id: string) => ItemDef = byId(items, "item");
/**
 * On KO's maps the game's own items are gone but parts of the game still name some (ranking
 * rewards, the store, the wheel): such a name reads as a blank material nobody is given.
 */
export const getItem = (id: string): ItemDef => {
  const made = enchantedItem(id);
  if (made) return made;
  if (!koOnly || itemIdSet.has(id)) return getItemStrict(id);
  return {
    id,
    name: id,
    kind: "material",
    slot: "none",
    levelReq: 1,
    value: 0,
    stats: {},
    icon: "ko:0",
    retired: true,
  } as unknown as ItemDef;
};
/** Whether an item template exists (a saved item of a set no longer loaded does not). */
export const hasItem = (id: string) => itemIdSet.has(id) || !!enchantedItem(id);
export const getNpc: (id: string) => NpcDef = byId(npcs, "npc");

/** A zone a travel gate can take you to. */
export interface Destination {
  zone: Zone;
  /** Level needed to travel there. */
  minLevel: number;
  /** Lowest and highest level of its regular mobs (bosses left out), null if none. */
  mobLevels: [number, number] | null;
}

/**
 * Where a gate in `fromId` can take a character of this race: every zone except the one you
 * are in and the other nation's homeland, lowest level first. Locked ones are listed too so
 * players see what is ahead; the server refuses them.
 */
export function travelDestinations(fromId: string, race: string): Destination[] {
  return zones
    .filter((z) => z.id !== fromId && !z.timed && !z.clash && (!z.nation || z.nation === race))
    .map((zone) => {
      const levels = zone.mobSpawns.map((s) => getMob(s.mob)).filter((m) => !m.boss);
      const lv = levels.map((m) => m.level);
      return {
        zone,
        minLevel: zone.minLevel ?? 1,
        mobLevels: lv.length > 0 ? ([Math.min(...lv), Math.max(...lv)] as [number, number]) : null,
      };
    })
    .sort((a, b) => a.minLevel - b.minLevel);
}

/** Where one gate leads: a gate with `only` goes to that zone alone, others anywhere. */
export function gateDestinations(fromId: string, index: number, race: string): Destination[] {
  const gate = getZone(fromId).portals?.[index];
  if (!gate) return [];
  if (!gate.only) return travelDestinations(fromId, race);
  const zone = getZone(gate.only);
  return [{ zone, minLevel: zone.minLevel ?? 1, mobLevels: mobLevelRange(zone) }];
}

function mobLevelRange(zone: Zone): [number, number] | null {
  const lv = zone.mobSpawns
    .map((s) => getMob(s.mob))
    .filter((m) => !m.boss)
    .map((m) => m.level);
  return lv.length > 0 ? [Math.min(...lv), Math.max(...lv)] : null;
}

/** A clan's grade (1 is the best) for its members' lifetime National Points added up. */
export function clanGrade(totalNp: number): number {
  let grade = social.clan.grades[0]?.grade ?? 5;
  for (const g of social.clan.grades) if (totalNp >= g.minNp) grade = g.grade;
  return grade;
}

/** National Points a clan needs for its next grade, or null at the top. */
export function clanNextGradeNp(totalNp: number): number | null {
  const next = social.clan.grades.find((g) => g.minNp > totalNp);
  return next ? next.minNp : null;
}

/** The Crystal Clash settings (shared by every bracket's arena), if the data has an arena. */
export const clash = zones.find((z) => z.clash)?.clash ?? null;

/** The arena of each Crystal Clash bracket, in bracket order. */
export const clashArenas: Zone[] = zones.filter((z) => z.clash);

/** The bracket a character of this level plays the Crystal Clash in, or -1 when none. */
export function clashBracketFor(level: number): number {
  return clash ? clash.brackets.findIndex((b) => level >= b.minLevel && level <= b.maxLevel) : -1;
}

export function zoneNpcs(zoneId: string): NpcDef[] {
  return npcs.filter((n) => n.zone === zoneId);
}

/** Equipment slots in display order; `ring` appears twice. */
export const EQUIP_SLOTS = [
  "weapon",
  "offhand",
  "head",
  "chest",
  "legs",
  "hands",
  "feet",
  "ring1",
  "ring2",
  "neck",
  "earring1",
  "earring2",
  "belt",
  "cape",
  "glow",
  "wings",
  "reward",
  "costume",
] as const;
export type EquipSlot = (typeof EQUIP_SLOTS)[number];
/** Bag slots (KO's maps too since 2026-10-28, owner: 56, eight rows of seven). */
export const BAG_SIZE = 56;
/** KO's chest trader in Moradon and the Treasure Chests she alone is for (local experiment). */
export const KO_CHEST_TRADER = "ko_moradon_moira";
export const KO_CHESTS: ReadonlySet<string> = new Set([
  "ko_379154000",
  "ko_379155000",
  "ko_379156000",
]);
/** The pieces KO's Chaotic Generator takes (local experiment; their rewards are server-only). */
export const KO_PIECES: ReadonlySet<string> = new Set(
  Object.keys((koJson as { generator?: Record<string, unknown> }).generator ?? {}),
);

/** Which equipment slots an item may go into. */
/** Items with a + ladder (weapons, armor, jewelry, cloaks): their + always shows, even +0. */
export function upgradable(templateId: string): boolean {
  return !!ladderFor(upgradeTables, getItem(templateId).slot);
}

/** The + an item comes with wherever it is made: weapons and armor +1, the rest +0. */
export function defaultPlus(templateId: string): number {
  const def = getItem(templateId);
  const kind = def.kind;
  // KO's weapons and armor come at +1 too, from merchants, the generator and the starter sets
  // (owner 2026-10-29: +0 means nothing); a piece KO names with its + ("Chitin Shell Pads (+5)")
  // keeps the one in its name
  if (koOnly && /\(\+\d+\)/.test(def.name)) return 0;
  return kind === "weapon" || kind === "armor" ? 1 : 0;
}

/** An item's name as players see it everywhere: "Iron Sword +1", "Copper Ring +0". */
/**
 * Local experiment: how far a character's plain attack reaches: a bow shoots from
 * `combat.koBowRange` (KO's arrows never run out here), anything else the class's weapon reach.
 */
export function attackReach(cls: ClassId, weaponTemplate?: string): number {
  const t = weaponTemplate && hasItem(weaponTemplate) ? getItem(weaponTemplate) : undefined;
  const bow = combat.koBowRange;
  return t?.bow && bow ? bow : getClass(cls).weapon.range;
}

/** How long a fallen player lies there before the return to town (`combat.koDeadMs` on KO's maps). */
export function deadMs(cfg: { koDeadMs?: number; playerRespawnMs: number } = combat): number {
  return cfg.koDeadMs ?? cfg.playerRespawnMs;
}

export function itemLabel(item: { templateId: string; plus: number; grade?: string }): string {
  const def = getItem(item.templateId);
  // KO names a magic or rare copy by its kind ("Strength Chitin Shell Pads")
  const kind =
    item.grade === "magic"
      ? def.koNames?.magic
      : item.grade === "rare" || item.grade === "unique"
        ? def.koNames?.rare
        : undefined;
  const pre = kind?.[Math.min(10, Math.max(1, item.plus)) - 1];
  const name = pre ? `${pre} ${def.name}` : def.name;
  // KO writes the plus as "(+3)" and leaves +0 out
  if (koOnly) return item.plus > 0 && upgradable(item.templateId) ? `${name}(+${item.plus})` : name;
  return upgradable(item.templateId) ? `${name} +${item.plus}` : name;
}

/** The + badge on an item slot: "+2", "+0" on anything with a ladder, "" otherwise. */
export function plusBadge(item: { templateId: string; plus: number }): string {
  // KO shows no +0
  if (koOnly && item.plus <= 0) return "";
  return upgradable(item.templateId) ? `+${item.plus}` : "";
}

export function slotsFor(item: ItemDef): EquipSlot[] {
  if (item.slot === "none") return [];
  if (item.slot === "ring") return ["ring1", "ring2"];
  if (item.slot === "earring") return ["earring1", "earring2"];
  // A store cloak takes the Cloak slot: one cloak at a time (owner decision 2026-10-02).
  if (item.slot === "mantle") return ["cape"];
  // a weapon for either hand: the right first, the left beside it (two daggers, KO)
  if (item.slot === "weapon" && item.dual) return ["weapon", "offhand"];
  return [item.slot];
}

/** Skills of a class ordered by unlock level (skill bar order). */
export function classSkills(classId: ClassId): SkillDef[] {
  return skills.filter((s) => s.classId === classId).sort((a, b) => a.unlockLevel - b.unlockLevel);
}

/** XP needed to go from `level` to `level + 1` (0 at the cap). */
export function xpToNext(level: number): number {
  return xpTable.find((r) => r.level === level)?.xp ?? 0;
}
